import { Subscribable, Subscription } from '@benzinga/subscribable';
import { safeMap, SafeError, SafePromise } from '@benzinga/safe-await';
import { SubscriptionRequest, SubscriptionRequestEvent } from './request';
import { AuthenticationManager, Session } from '@benzinga/session';
import { UserSubscription, SubscriptionStatus } from './entities';
import { LineItemPrice } from './entities';
import { deepEqual, oneRequestAtATime } from '@benzinga/utils';

export type SubscriptionsManagerEvent = SubscriptionRequestEvent | UserSubscriptionsChangedEvent;

interface UserSubscriptionsChangedEvent {
  userSubscriptions: UserSubscription[] | undefined;
  type: 'subscription:user_subscriptions_changed';
}

export class SubscriptionsManager extends Subscribable<SubscriptionsManagerEvent> {
  private userSubscriptions: undefined | UserSubscription[]; // this should be moved into storage later
  private request: SubscriptionRequest;
  private requestSubscription?: Subscription<SubscriptionRequest>;
  private getSubscriptionUnified = oneRequestAtATime(async () => {
    const subscriptions = await this.request.getSubscriptions();
    this.setSubscriptions(subscriptions.ok);
    return subscriptions;
  });

  constructor(session: Session) {
    super();
    this.request = new SubscriptionRequest(session);
    const authenticationManger = session.getManager(AuthenticationManager);
    const authenticationMangerSubscription = authenticationManger.subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          if (event.auth?.user?.subscriptions) {
            this.setSubscriptions(event.auth?.user?.subscriptions);
          } else {
            this.getSubscriptions(true);
          }
          break;
      }
    });
    session.onPreSessionClose(() => authenticationMangerSubscription.unsubscribe());

    if (authenticationManger.isLoggedIn()) {
      authenticationManger.getAuthSession().then(session => {
        if (session?.ok?.user?.subscriptions) {
          this.setSubscriptions(session?.ok?.user?.subscriptions);
        } else {
          this.getSubscriptions(true);
        }
      });
    }
  }

  public static getName = () => 'benzinga-subscriptions';

  public getSubscriptions = async (force?: boolean): SafePromise<UserSubscription[]> => {
    if (force || this.userSubscriptions === undefined) {
      return this.getSubscriptionUnified();
    } else {
      return { ok: this.userSubscriptions };
    }
  };

  public getTrialingSubscription = (product?: string): UserSubscription | null => {
    const subscriptions = this.getSubscriptionsStored(product);
    return subscriptions?.find(s => s.status === SubscriptionStatus.trialing) ?? null;
  };

  public isPayedUserStored = (): boolean | undefined => {
    const subscriptions = this.userSubscriptions;
    return subscriptions?.some(s => s.status === SubscriptionStatus.active);
  };

  public isPayedUser = async (): SafePromise<boolean> => {
    const subscriptions = this.getSubscriptions();
    return safeMap(subscriptions, subscriptions => subscriptions.some(s => s.status === SubscriptionStatus.active));
  };

  public hasTrialed = (product?: string): boolean => {
    const subscriptions = this.getSubscriptionsStored(product);
    return subscriptions?.some(s => typeof s.trialEnd === 'string' && new Date(s.trialEnd) < new Date()) ?? false;
  };

  public isTrialing = (product?: string): boolean => {
    const subscriptions = this.getSubscriptionsStored(product);
    return subscriptions?.some(s => s.status === SubscriptionStatus.trialing) ?? false;
  };

  public createSubscription = (plan: string, trial: boolean, coupon?: string): SafePromise<UserSubscription> => {
    return this.request.createSubscription(plan, trial, coupon);
  };

  public getSubscriptionsStored = (product?: string): UserSubscription[] | undefined =>
    product === undefined
      ? this.userSubscriptions
      : this.userSubscriptions?.filter(s => product === undefined || s.product === product);

  public getActiveSubscriptions = async (product?: string): SafePromise<UserSubscription[]> => {
    const subscriptions = this.getSubscriptions();
    return safeMap(subscriptions, subscriptions =>
      subscriptions?.filter(s => (product === undefined || s.product === product) && s.status === 'active'),
    );
  };

  public getActiveSubscriptionsForCancel = async (product?: string): SafePromise<UserSubscription[]> => {
    const subscriptions = this.getSubscriptions();
    return safeMap(subscriptions, subscriptions =>
      subscriptions?.filter(
        s => (product === undefined || s.product === product) && s.status === 'active' && s.cancelAtPeriodEnd,
      ),
    );
  };

  public getCanceledSubscriptionsStored = (product?: string): UserSubscription[] | undefined => {
    const subscriptions = this.getSubscriptionsStored(product)?.filter(s => s.status === 'canceled') || [];
    if (subscriptions.length === 0) {
      return undefined;
    }
    return subscriptions.sort(
      (a, b) => new Date(b.trialEnd as string).getTime() - new Date(a.trialEnd as string).getTime(),
    );
  };

  public getTrialEndedSubscriptionStored = (product?: string): UserSubscription | undefined => {
    return this.getCanceledSubscriptionsStored(product)?.filter(s => s.trialEnd != null)?.[0];
  };

  public getCanceledSubscriptionStored = (product?: string): UserSubscription | undefined => {
    return this.getCanceledSubscriptionsStored(product)?.[0];
  };

  public getActiveSubscription = async (product?: string): SafePromise<UserSubscription | undefined> => {
    const subscriptions = this.getActiveSubscriptions(product);
    return safeMap(subscriptions, subscriptions => subscriptions.find(s => s.status === 'active'));
  };

  public getActiveSubscriptionsStored = (product?: string): UserSubscription[] | undefined => {
    const subscriptions = this.getSubscriptionsStored(product);
    return subscriptions?.filter(s => s.status === 'active');
  };

  public getActiveSubscriptionsStoredForCancelStored = (product?: string): UserSubscription[] | undefined => {
    const subscriptions = this.getSubscriptionsStored(product);
    return subscriptions?.filter(s => s.cancelAtPeriodEnd);
  };

  public getActiveSubscriptionStored = (product?: string): UserSubscription | undefined => {
    return this.getActiveSubscriptionsStored(product)?.[0];
  };

  public getSubscription = async (uuid: string): SafePromise<UserSubscription> => {
    const subscriptions = await this.getSubscriptions();
    if (subscriptions.err) {
      return { err: subscriptions.err };
    } else {
      const subscription = subscriptions.ok.find(s => s.uuid === uuid);
      if (subscription) {
        return { ok: subscription };
      } else {
        return { err: new SafeError(`the subscription uuid ${uuid} does not exist`, 'subscription') };
      }
    }
  };

  public upgradeSubscription = (
    subscription: UserSubscription,
    selectedPlan: string,
    card?: string,
    coupon?: string,
    isFeatureUpdate?: boolean,
  ): SafePromise<LineItemPrice> => {
    return this.request.updateSubscription(subscription.uuid, selectedPlan, card, coupon, isFeatureUpdate);
  };

  public deleteSubscription = (uuid: string): SafePromise<LineItemPrice> => {
    return this.request.deleteSubscription(uuid);
  };

  public restartSubscription = (uuid: string): SafePromise<UserSubscription> => {
    return this.request.restartSubscription(uuid);
  };

  public hasSubscription = (productId: string | string[]) => {
    if (Array.isArray(productId)) {
      return productId.some((p: string) => {
        return this.checkSubscription(p);
      });
    } else {
      return this.checkSubscription(productId);
    }
  };

  protected checkSubscription = (productId: string): boolean | undefined => {
    return this.getActiveSubscriptionsStored()?.some?.(p => {
      return p.product == productId;
    });
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }

  private setSubscriptions = (userSubscriptions: UserSubscription[] | undefined): boolean => {
    if (deepEqual(userSubscriptions, this.userSubscriptions) === false) {
      this.userSubscriptions = userSubscriptions;
      this.dispatch({
        type: 'subscription:user_subscriptions_changed',
        userSubscriptions: this.userSubscriptions,
      });
      return true;
    }
    return false;
  };
}
